import React, { useEffect, useState } from "react";
import { Box, Card, Typography, Grid, Paper, Skeleton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { fetchCompanies, setSummaryStats } from "../../redux/Dashboard/dashboardActions";
import { getRequest } from "../../globalhelper/helper";

const StatCard = ({ title, value, icon, color, subtitle, bgColor, loading }) => (
    <Card sx={{
        p: 1.5,
        height: "100%",
        borderRadius: 1.5,
        boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.06)"
        }
    }}>
        <Box sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: bgColor || "#f8fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            {loading ? (
                <Skeleton variant="circular" width={18} height={18} />
            ) : (
                React.cloneElement(icon, { sx: { color: color, fontSize: 18 } })
            )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 1 }}>
                {title}
            </Typography>
            {loading ? (
                <Skeleton width="80%" height={32} sx={{ mb: 0.5 }} />
            ) : (
                <Typography variant="h6" sx={{
                    color: "#475569",
                    fontWeight: 700,
                    fontSize: value?.toString().length > 10 ? "0.95rem" : "1.1rem",
                    mb: 0.1,
                    whiteSpace: "nowrap"
                }}>
                    {typeof value === 'number' && title !== 'Invoices Processed' ? (
                        value >= 10000000
                            ? `₹${(value / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
                            : value >= 100000
                                ? `₹${(value / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`
                                : `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                    ) : value}
                </Typography>
            )}
            {subtitle && (
                loading ? (
                    <Skeleton width="40%" height={14} />
                ) : (
                    <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600, fontSize: "0.7rem" }}>
                        {subtitle}
                    </Typography>
                )
            )}
        </Box>
    </Card>
);

const SummaryStats = ({ company, peid, fromDate, toDate }) => {
    const dispatch = useDispatch();
    const { summary, project } = useSelector((state) => state.dashboard);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (company) {
            setLoading(true);
            // Calculate Budget stats from Redux (project.reports) as requested
            const reports = project.reports || [];
            const totalBudget = reports.reduce((sum, item) => sum + (item.Budget || 0), 0);
            const actualExpense = reports.reduce((sum, item) => sum + (item.Actuals || 0), 0);
            const budgetStats = {
                totalBudget,
                actualExpense,
                remainingBudget: totalBudget - actualExpense
            };

            dispatch(setSummaryStats({
                ...summary,
                ...budgetStats
            }));

            // Simulate brief calculation delay or wait for data
            const timer = setTimeout(() => {
                setLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [company, peid, fromDate, toDate, project.reports]);

    const stats = [
        {
            title: "Total Budget",
            value: summary.totalBudget,
            icon: <AccountBalanceWalletIcon />,
            color: "#6366f1",
            bgColor: "#eef2ff",
        },
        {
            title: "Actual Expense",
            value: summary.actualExpense,
            icon: <TrendingUpIcon />,
            color: "#10b981",
            bgColor: "#ecfdf5",
        },
        {
            title: "Remaining Budget",
            value: summary.remainingBudget,
            icon: <ReceiptLongIcon />,
            color: "#f59e0b",
            bgColor: "#fffbeb",
        },
        {
            title: "Invoices Processed",
            value: summary.invoicesProcessed,
            icon: <CheckCircleOutlineIcon />,
            color: "#3b82f6",
            bgColor: "#eff6ff",
            subtitle: "Total processed"
        },
        {
            title: "Pending GRNs | POs",
            value: `${summary.pendingGRNs || 0} | ${summary.pendingPOs || 0}`,
            icon: <HourglassEmptyIcon />,
            color: "#ef4444",
            bgColor: "#fef2f2",
            subtitle: "Awaiting action"
        }
    ];

    return (
        <Grid container spacing={2.5} sx={{ mb: 2, width: "100%" }}>
            {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={2.4} key={index}>
                    <StatCard {...stat} loading={loading} />
                </Grid>
            ))}
        </Grid>
    );
};

export default SummaryStats
